import { ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '@/hooks/use-api';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import FileUpload from '@/components/FileUpload';
import { useAvatarCrop } from '@/components/media/ImageCropDialog';

/**
 * Reusable photo field: live preview + R2 Upload button + paste-a-URL input.
 * Uploads run through a 1:1 crop + auto-compress dialog so every profile
 * photo / logo lands square and lightweight.
 */
export const AvatarUploadField = ({
  value, onChange, folder = 'avatars', label = 'Photo',
  shape = 'circle', fallback = 'U', testid,
}) => {
  const api = useApi();
  const { transform, dialog } = useAvatarCrop();
  const rounded = shape === 'circle' ? 'rounded-full' : 'rounded-xl';

  const onUploaded = async ({ key }) => {
    try { onChange(await api.publicUrl(key)); }
    catch (e) { toast.error('Upload failed', { description: e.message }); }
  };

  return (
    <div className="flex items-center gap-4">
      <Avatar className={`h-14 w-14 ${rounded} border border-border shrink-0`}>
        <AvatarImage src={value} className="object-cover" />
        <AvatarFallback className={rounded}>
          {fallback ? <span className="text-sm font-medium text-muted-foreground">{fallback}</span> : <ImageIcon className="h-5 w-5 text-muted-foreground" />}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <Label className="text-xs">{label}</Label>
        <div className="flex flex-col sm:flex-row gap-2 mt-1.5">
          <div className="sm:w-36 shrink-0">
            <FileUpload folder={folder} accept="image/*" label="Upload" resetAfterUpload transform={transform} testid={testid ? `${testid}-upload` : undefined} onUploaded={onUploaded} />
          </div>
          <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="or paste image URL" className="flex-1 min-w-0" data-testid={testid ? `${testid}-url` : undefined} />
        </div>
      </div>
      {dialog}
    </div>
  );
};

export default AvatarUploadField;
